"use client"

import { CheckCircle2, XCircle, AlertTriangle, Info } from "lucide-react"

import {
  Toast,
  ToastClose,
  ToastDescription,
  ToastProvider,
  ToastTitle,
  ToastViewport,
} from "@/components/ui/toast"
import { useToast } from "@/components/ui/use-toast"
import { cn } from "@/lib/utils"

const VARIANT_ICONS = {
  success: CheckCircle2,
  error: XCircle,
  destructive: XCircle,
  warning: AlertTriangle,
  info: Info,
} as const

const VARIANT_ICON_COLOR = {
  success: "text-success-600",
  error: "text-error-600",
  destructive: "text-error-600",
  warning: "text-amber-600",
  info: "text-blue-700",
} as const

type IconVariant = keyof typeof VARIANT_ICONS

export function Toaster() {
  const { toasts } = useToast()

  return (
    <ToastProvider>
      {toasts.map(function ({ id, title, description, action, variant, ...props }) {
        const key = variant as IconVariant
        const Icon = key && key in VARIANT_ICONS ? VARIANT_ICONS[key] : null
        return (
          <Toast key={id} variant={variant} {...props}>
            {Icon && (
              <Icon
                className={cn("mt-0.5 h-5 w-5 shrink-0", VARIANT_ICON_COLOR[key])}
                aria-hidden="true"
              />
            )}
            <div className="grid flex-1 gap-1">
              {title && <ToastTitle>{title}</ToastTitle>}
              {description && (
                <ToastDescription>{description}</ToastDescription>
              )}
            </div>
            {action}
            <ToastClose />
          </Toast>
        )
      })}
      <ToastViewport />
    </ToastProvider>
  )
}
